import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import { Helmet } from "react-helmet";
import { HiOutlineKey } from "react-icons/hi";
import { MdPhone, MdWarning } from "react-icons/md";
import apiRequest from "../../../utils/apiRequest";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { BRAND_NAME } from "../../../utils/constant";
import { HOME_ROUTE } from "../../../routes/web/rootRoutes";
import logo from "../../../assets/logo.jpg";
import authManager from "../../../utils/authManager";
import { LogoMain } from "../../../utils/helper";
import Button from "../../../components/ui/utils/Button";
import InputField from "../../../components/ui/form/InputField";

const DeleteAccount = () => {
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();

  // OTP Refs
  const otpRef0 = useRef(null);
  const otpRef1 = useRef(null);
  const otpRef2 = useRef(null);
  const otpRef3 = useRef(null);
  const otpRefs = [otpRef0, otpRef1, otpRef2, otpRef3];

  const formik = useFormik({
    initialValues: {
      mobile: "",
      otp: "",
      password: "",
    },

    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        // STEP 1: SEND OTP
        if (!isOtpSent) {
          if (values.mobile.length !== 10) {
            toast.error("Enter a valid 10-digit mobile number");
            return;
          }

          await apiRequest.sendOtp({
            body: { mobile: values.mobile, type: "delete_account" },
          });

          toast.success("OTP sent successfully!");
          setIsOtpSent(true);
          setTimer(59);
          setServerErrors({});
          return;
        }

        // STEP 2: VERIFY OTP & DELETE ACCOUNT
        if (!showConfirmation) {
          setShowConfirmation(true);
          return;
        }

        const response = await apiRequest.deleteAccount({
          body: {
            mobile: values.mobile,
            otp: values.otp,
            password: values.password,
          },
        });

        if (response?.data?.response) {
          authManager.clearAuth();
          toast.success("Account deleted successfully!");
          navigate(HOME_ROUTE);
        }

        setServerErrors({});
      } catch (error) {
        if (error?.status === 400) {
          setServerErrors(error?.response?.data?.data || {});
        }
        toast.error(
          String(error?.response?.data?.message || "Something went wrong")
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  // TIMER
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // OTP INPUT HANDLER
  const handleOtpChange = (value, index) => {
    const otpArray = formik.values.otp.padEnd(4, " ").split("");
    otpArray[index] = value;
    const newOtp = otpArray.join("").replace(/\s/g, "");
    formik.setFieldValue("otp", newOtp);

    if (value && index < 3) otpRefs[index + 1].current?.focus();
  };

  return (
    <>
      <Helmet>
        <title>Delete Account - {BRAND_NAME}</title>
      </Helmet>

      <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-red-50 via-white to-orange-50">
        {/* LEFT SIDE */}
        <div className="hidden md:flex flex-col justify-center items-center w-1/2 bg-gradient-to-br from-red-600 via-orange-600 to-red-700 text-white relative">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
          <div className="z-10 text-center px-8">
            <img
              src={logo}
              alt="Brand Logo"
              className="mx-auto mb-6 w-28 h-28 rounded-full border-4 border-white/40 shadow-lg"
            />
            <h1 className="text-4xl font-bold mb-3">Account Deletion</h1>
            <p className="text-lg text-white/90">
              We're sorry to see you go. This action cannot be undone.
            </p>
          </div>
        </div>

        {/* RIGHT SIDE - FORM */}
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md bg-white/80 backdrop-blur-xl border border-white/40 shadow-2xl rounded-3xl p-8">
            <div className="text-center mb-8">
              <img
                className="h-[100px] rounded-full border-red-500 shadow mx-auto"
                src={LogoMain}
                alt="logo"
              />
              <h2 className="text-3xl font-bold text-gray-800">Delete Account</h2>
              <p className="text-gray-500 text-sm mt-1">
                Verify your identity to delete your account
              </p>
            </div>

            {/* WARNING BANNER */}
            {!showConfirmation && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
                <div className="flex items-start">
                  <MdWarning className="text-red-500 text-2xl mr-3 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-red-800">Warning</h3>
                    <p className="text-xs text-red-700 mt-1">
                      Deleting your account is permanent and cannot be undone. All your data will be lost.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* CONFIRMATION MESSAGE */}
            {showConfirmation && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded-lg">
                <div className="flex items-start">
                  <MdWarning className="text-yellow-600 text-2xl mr-3 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-yellow-800">Final Confirmation</h3>
                    <p className="text-xs text-yellow-700 mt-1">
                      Are you absolutely sure? Click "Confirm Delete" to permanently delete your account.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div onSubmit={formik.handleSubmit} className="space-y-5">
              {/* MOBILE NUMBER */}
              <div className="relative">
                <label className="text-sm font-medium text-gray-700">
                  Mobile Number
                </label>

                <div className="flex items-center gap-2">
                  <input
                    id="mobile"
                    type="tel"
                    maxLength={10}
                    placeholder="Enter mobile number"
                    value={formik.values.mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isOtpSent}
                    className="flex-1 border rounded-lg px-3 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />

                  {!isOtpSent && (
                    <button
                      type="button"
                      disabled={formik.values.mobile.length !== 10}
                      onClick={async () => {
                        try {
                          setIsLoading(true);
                          await apiRequest.sendOtp({
                            body: {
                              mobile: formik.values.mobile,
                              type: "delete_account",
                            },
                          });
                          toast.success("OTP sent!");
                          setIsOtpSent(true);
                          setTimer(59);
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.message || "Error sending OTP"
                          );
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      className={`px-4 py-2 rounded-lg text-white font-semibold ${
                        formik.values.mobile.length === 10
                          ? "bg-red-600 hover:bg-red-700"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      Send OTP
                    </button>
                  )}
                </div>
                {serverErrors?.mobile && (
                  <p className="text-red-500 text-xs mt-1">{serverErrors.mobile}</p>
                )}
              </div>

              {/* OTP INPUT */}
              {isOtpSent && (
                <div className="mt-3">
                  <label className="text-sm font-medium text-gray-700 block text-center mb-2">
                    Enter OTP
                  </label>

                  <div className="flex justify-center gap-3">
                    {otpRefs.map((ref, index) => (
                      <input
                        key={index}
                        ref={ref}
                        maxLength={1}
                        type="text"
                        className="w-12 h-12 text-xl text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500"
                        value={formik.values.otp[index] || ""}
                        onChange={(e) =>
                          handleOtpChange(e.target.value.replace(/\D/, ""), index)
                        }
                      />
                    ))}
                  </div>

                  {/* TIMER / RESEND */}
                  <div className="text-center mt-3">
                    {timer > 0 ? (
                      <span className="text-sm text-gray-500">
                        Resend OTP in {timer}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="text-sm text-red-600 hover:underline"
                        onClick={async () => {
                          try {
                            await apiRequest.sendOtp({
                              body: {
                                mobile: formik.values.mobile,
                                type: "delete_account",
                              },
                            });
                            toast.success("OTP resent!");
                            setTimer(59);
                          } catch (err) {
                            toast.error("Failed to resend OTP");
                          }
                        }}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* PASSWORD */}
              {isOtpSent && (
                <div className="relative">
                  <InputField
                    label="Password"
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && formik.errors.password}
                    serverError={serverErrors?.password}
                  />
                  <HiOutlineKey className="absolute right-3 top-9 text-gray-400" />
                </div>
              )}

              {/* SUBMIT BUTTON */}
              <Button
                type="submit"
                isLoading={isLoading}
                loadingText={showConfirmation ? "Deleting Account..." : "Verifying..."}
                className={`w-full py-3 font-semibold rounded-xl shadow ${
                  showConfirmation
                    ? "bg-red-700 hover:bg-red-800"
                    : "bg-red-600 hover:bg-red-700"
                } text-white`}
                onClick={formik.handleSubmit}
              >
                {!isOtpSent
                  ? "Get OTP"
                  : showConfirmation
                  ? "Confirm Delete Account"
                  : "Verify & Continue"}
              </Button>

              {/* CANCEL BUTTON */}
              {showConfirmation && (
                <button
                  type="button"
                  onClick={() => setShowConfirmation(false)}
                  className="w-full py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-xl"
                >
                  Cancel
                </button>
              )}
            </div>

            <p className="text-center text-sm text-gray-600 mt-6">
              Changed your mind?
              <button
                onClick={() => navigate(-1)}
                className="text-blue-600 hover:underline ml-1 font-semibold"
              >
                Go Back
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteAccount;